import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { startOfWeek, endOfWeek, parseISO, isWithinInterval, format, subWeeks } from 'date-fns';
import { Plus, Trash2, Printer, DollarSign, Users, FileText, Pencil, Check, X, Settings, CreditCard, Download, TrendingUp, Info } from 'lucide-react';
import AdminSettings from './AdminSettings';
import OnboardingModal from '../components/OnboardingModal';
import Payments from './Payments';
import Reports from './Reports';

// Onboarding hint component
function Hint({ id, children }) {
  const key = `ddp_hint_${id}`;
  const [visible, setVisible] = useState(() => localStorage.getItem(key) !== 'dismissed');
  if (!visible) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
      <Info size={14} color="var(--accent)" style={{ marginTop: 1, flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{children}</span>
      <button onClick={() => { localStorage.setItem(key, 'dismissed'); setVisible(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, flexShrink: 0 }}><X size={14} /></button>
    </div>
  );
}

function getRate(customer, unitType) {
  if (unitType === 'Tons') return parseFloat(customer.tonRate || 0);
  if (unitType === 'Loads') return parseFloat(customer.loadRate || 0);
  return parseFloat(customer.hourlyRate || 0);
}

function getAmount(log, customer) {
  const qty = parseFloat(log.quantity || log.hours || 0);
  const rate = log.customRate ? parseFloat(log.customRate) : getRate(customer, log.unitType || 'Hours');
  return qty * rate;
}

function CustomerManager() {
  const { customers, addCustomer, updateCustomer, deleteCustomer } = useApp();
  const [newName, setNewName] = useState('');
  const [newHourlyRate, setNewHourlyRate] = useState('');
  const [newTonRate, setNewTonRate] = useState('');
  const [newLoadRate, setNewLoadRate] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newState, setNewState] = useState('');
  const [newZip, setNewZip] = useState('');
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newName) { alert('Please enter a customer name.'); return; }
    if (!newHourlyRate && !newTonRate && !newLoadRate) { alert('Please enter at least one rate.'); return; }
    setAdding(true);
    await addCustomer({
      name: newName,
      hourlyRate: newHourlyRate ? parseFloat(newHourlyRate) : null,
      tonRate: newTonRate ? parseFloat(newTonRate) : null,
      loadRate: newLoadRate ? parseFloat(newLoadRate) : null,
      address: newAddress, city: newCity, state: newState, zip: newZip,
    });
    setNewName(''); setNewHourlyRate(''); setNewTonRate(''); setNewLoadRate('');
    setNewAddress(''); setNewCity(''); setNewState(''); setNewZip('');
    setAdding(false);
  };

  const startEdit = (c) => { setEditing(c.id); setEditForm({ ...c }); };
  const saveEdit = (id) => { updateCustomer(id, editForm); setEditing(null); };
  const setEF = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div>
      <h2 style={{ fontSize: 20, marginBottom: 16 }}>Customers & Rates</h2>
      <Hint id="customers">Add your customers here first. Enter their name and the rate you charge per hour, ton, or load. You must add a customer before drivers can log work. After adding, your new customer appears instantly below.</Hint>
      <div className="card" style={{ marginBottom: 16 }}>
        {/* Hidden inputs to trick Safari autofill */}
        <input type="text" style={{ display: 'none' }} />
        <input type="password" style={{ display: 'none' }} />
        <div className="field">
          <label>Customer Name *</label>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Company name" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" name="cust-name-x7k" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Rates (fill in whichever apply)</label>
          <div className="grid-3" style={{ marginTop: 6 }}>
            <div className="field" style={{ marginBottom: 0 }}><label>$/Hour</label><input type="number" min="0" step="0.01" value={newHourlyRate} onChange={e => setNewHourlyRate(e.target.value)} placeholder="0.00" autoComplete="off" autoCorrect="off" spellCheck="false" /></div>
            <div className="field" style={{ marginBottom: 0 }}><label>$/Ton</label><input type="number" min="0" step="0.01" value={newTonRate} onChange={e => setNewTonRate(e.target.value)} placeholder="0.00" autoComplete="off" autoCorrect="off" spellCheck="false" /></div>
            <div className="field" style={{ marginBottom: 0 }}><label>$/Load</label><input type="number" min="0" step="0.01" value={newLoadRate} onChange={e => setNewLoadRate(e.target.value)} placeholder="0.00" autoComplete="off" autoCorrect="off" spellCheck="false" /></div>
          </div>
        </div>
        <div className="field"><label>Address (optional)</label><input value={newAddress} onChange={e => setNewAddress(e.target.value)} placeholder="---" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" /></div>
        <div className="grid-3">
          <div className="field" style={{ marginBottom: 0 }}><label>City</label><input value={newCity} onChange={e => setNewCity(e.target.value)} placeholder="---" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" /></div>
          <div className="field" style={{ marginBottom: 0 }}><label>State</label><input value={newState} onChange={e => setNewState(e.target.value)} placeholder="---" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" /></div>
          <div className="field" style={{ marginBottom: 0 }}><label>Zip</label><input value={newZip} onChange={e => setNewZip(e.target.value)} placeholder="---" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" /></div>
        </div>
        <button type="button" onClick={handleAdd} className="btn-primary" disabled={adding} style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> {adding ? 'Adding...' : 'Add Customer'}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {customers.map(c => (
          <div key={c.id} className="card">
            {editing === c.id ? (
              <div>
                <div className="field"><label>Customer Name</label><input value={editForm.name || ''} onChange={setEF('name')} autoComplete="off" autoCorrect="off" /></div>
                <div className="grid-3">
                  <div className="field"><label>$/Hour</label><input type="number" value={editForm.hourlyRate || ''} onChange={setEF('hourlyRate')} placeholder="0.00" autoComplete="off" autoCorrect="off" spellCheck="false" /></div>
                  <div className="field"><label>$/Ton</label><input type="number" value={editForm.tonRate || ''} onChange={setEF('tonRate')} placeholder="0.00" autoComplete="off" autoCorrect="off" spellCheck="false" /></div>
                  <div className="field"><label>$/Load</label><input type="number" value={editForm.loadRate || ''} onChange={setEF('loadRate')} placeholder="0.00" autoComplete="off" autoCorrect="off" spellCheck="false" /></div>
                </div>
                <div className="field"><label>Address</label><input value={editForm.address || ''} onChange={setEF('address')} autoComplete="off" /></div>
                <div className="grid-3">
                  <div className="field"><label>City</label><input value={editForm.city || ''} onChange={setEF('city')} autoComplete="off" /></div>
                  <div className="field"><label>State</label><input value={editForm.state || ''} onChange={setEF('state')} autoComplete="off" /></div>
                  <div className="field"><label>Zip</label><input value={editForm.zip || ''} onChange={setEF('zip')} autoComplete="off" /></div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary" style={{ padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => saveEdit(c.id)}><Check size={13} /> Save</button>
                  <button className="btn-secondary" style={{ padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => setEditing(null)}><X size={13} /> Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 600, fontSize: 15 }}>{c.name}</span>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 10 }}>
                    {c.hourlyRate && <span>${c.hourlyRate}/hr</span>}
                    {c.tonRate && <span>${c.tonRate}/ton</span>}
                    {c.loadRate && <span>${c.loadRate}/load</span>}
                  </div>
                </div>
                <button className="btn-secondary" style={{ padding: '5px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => startEdit(c)}><Pencil size={12} /> Edit</button>
                <button className="btn-danger" onClick={() => deleteCustomer(c.id)}><Trash2 size={12} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WeeklyInvoice() {
  const { logs, customers, getInvoiceNumber, settings } = useApp();
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [invoiceNums, setInvoiceNums] = useState({});
  const [pdfLoading, setPdfLoading] = useState(null);

  const refDate = subWeeks(new Date(), weekOffset);
  const weekStart = startOfWeek(refDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(refDate, { weekStartsOn: 1 });
  const thisWeekKey = format(weekStart, 'yyyy-MM-dd');

  const weekLogs = logs.filter(l => {
    try { return isWithinInterval(parseISO(l.date), { start: weekStart, end: weekEnd }); }
    catch (e) { return false; }
  });

  const customerFilter = selectedCustomer === 'all' ? weekLogs : weekLogs.filter(l => l.customerId === selectedCustomer);

  // Sort logs by date ASC then truck number ASC
  const sortLogs = (logsArr) => [...logsArr].sort((a, b) => {
    if (a.date < b.date) return -1;
    if (a.date > b.date) return 1;
    const truckA = parseInt(a.truckNumber) || a.truckNumber || '';
    const truckB = parseInt(b.truckNumber) || b.truckNumber || '';
    if (truckA < truckB) return -1;
    if (truckA > truckB) return 1;
    return 0;
  });

  const byCustomer = customers.map(c => {
    const cLogs = sortLogs(customerFilter.filter(l => l.customerId === c.id));
    const subtotal = cLogs.reduce((sum, l) => sum + getAmount(l, c), 0);
    return { ...c, logs: cLogs, subtotal };
  }).filter(c => c.logs.length > 0);

  const grandTotal = byCustomer.reduce((sum, c) => sum + c.subtotal, 0);

  useEffect(() => {
    byCustomer.forEach(async c => {
      const key = c.id + '_' + thisWeekKey;
      if (!invoiceNums[key]) {
        const num = await getInvoiceNumber(c.id, thisWeekKey);
        setInvoiceNums(prev => ({ ...prev, [key]: num }));
      }
    });
  }, [weekOffset, selectedCustomer, logs, customers]);

  const handleDownloadPDF = async (c, invoiceNum) => {
    setPdfLoading(c.id);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      const margin = 48;
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = margin;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text(settings.companyName || 'Daily Driver Pulse', margin, y);
      y += 20;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      if (settings.companyAddress) { doc.text(settings.companyAddress, margin, y); y += 14; }
      if (settings.companyCity || settings.companyState || settings.companyZip) {
        doc.text([settings.companyCity, settings.companyState, settings.companyZip].filter(Boolean).join(', '), margin, y); y += 14;
      }
      if (settings.companyPhone) { doc.text(settings.companyPhone, margin, y); y += 14; }
      if (settings.companyEmail) { doc.text(settings.companyEmail, margin, y); y += 14; }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(0, 0, 0);
      doc.text('INVOICE', pageWidth - margin, margin, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Invoice #${invoiceNum}`, pageWidth - margin, margin + 20, { align: 'right' });
      doc.text(`Week of ${format(weekStart, 'MMM d')} to ${format(weekEnd, 'MMM d, yyyy')}`, pageWidth - margin, margin + 34, { align: 'right' });
      doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy')}`, pageWidth - margin, margin + 48, { align: 'right' });

      y = Math.max(y, margin + 70) + 10;
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text('BILL TO', margin, y);
      y += 14;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(c.name, margin, y);
      y += 16;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      if (c.address) { doc.text(c.address, margin, y); y += 14; }
      if (c.city || c.state || c.zip) { doc.text([c.city, c.state, c.zip].filter(Boolean).join(', '), margin, y); y += 14; }
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total: $${c.subtotal.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
      y += 30;

      doc.setDrawColor(0, 0, 0);
      doc.line(margin, y, pageWidth - margin, y);
      y += 14;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text('DATE', margin, y);
      doc.text('JOB #', margin + 60, y);
      doc.text('TRUCK', margin + 110, y);
      doc.text('WORK', margin + 155, y);
      doc.text('NOTES', margin + 290, y);
      doc.text('TYPE', margin + 370, y);
      doc.text('QTY', margin + 415, y);
      doc.text('RATE', pageWidth - margin - 80, y);
      doc.text('AMOUNT', pageWidth - margin, y, { align: 'right' });
      y += 8;
      doc.line(margin, y, pageWidth - margin, y);
      y += 14;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      for (const l of c.logs) {
        const unitType = l.unitType || 'Hours';
        const qty = parseFloat(l.quantity || l.hours || 0);
        const rate = l.customRate ? parseFloat(l.customRate) : getRate(c, unitType);
        const amt = (qty * rate).toFixed(2);
        const work = l.workDescription ? (l.workDescription.length > 18 ? l.workDescription.substring(0, 18) + '...' : l.workDescription) : '';
        const notes = l.notes ? (l.notes.length > 14 ? l.notes.substring(0, 14) + '...' : l.notes) : '-';
        doc.text(l.date || '', margin, y);
        doc.text(l.jobNumber || '-', margin + 60, y);
        doc.text(l.truckNumber || '-', margin + 110, y);
        doc.text(work, margin + 155, y);
        doc.text(notes, margin + 290, y);
        doc.text(unitType, margin + 370, y);
        doc.text(String(qty), margin + 415, y);
        doc.text(`$${rate.toFixed(2)}`, pageWidth - margin - 80, y);
        doc.text(`$${amt}`, pageWidth - margin, y, { align: 'right' });
        y += 16;
        doc.setDrawColor(230, 230, 230);
        doc.line(margin, y - 4, pageWidth - margin, y - 4);
        if (y > 700) { doc.addPage(); y = margin; }
      }

      y += 10;
      doc.setDrawColor(0, 0, 0);
      doc.line(margin, y, pageWidth - margin, y);
      y += 16;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Due: $${c.subtotal.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });

      // Full page photos
      const logsWithPhotos = c.logs.filter(l => l.photoPreview);
      for (const l of logsWithPhotos) {
        doc.addPage();
        const imgPageWidth = doc.internal.pageSize.getWidth();
        const imgPageHeight = doc.internal.pageSize.getHeight();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`Work Slip — ${l.date} · Truck #${l.truckNumber || '-'}`, margin, margin - 10);
        try {
          const src = l.photoPreview;
          const isJpeg = src.includes('data:image/jpeg') || src.includes('data:image/jpg');
          doc.addImage(src, isJpeg ? 'JPEG' : 'PNG', margin, margin, imgPageWidth - margin * 2, imgPageHeight - margin * 2, undefined, 'FAST');
        } catch (imgErr) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(150, 150, 150);
          doc.text('Photo could not be embedded.', margin, margin + 20);
        }
      }

      doc.save(`Invoice-${c.name.replace(/\s+/g, '-')}-${invoiceNum}.pdf`);
    } catch (e) {
      alert('Could not generate PDF. Please try again.');
      console.error(e);
    }
    setPdfLoading(null);
  };

  return (
    <>
      <style>{`
        @media print {
          body { background: #fff !important; color: #000 !important; }
          nav, .no-print, .admin-header, .admin-tabs { display: none !important; }
          .page-wide { padding: 0 !important; max-width: 100% !important; }
          .print-invoice .card { background: #fff !important; border: 1px solid #ccc !important; color: #000 !important; page-break-inside: avoid; margin-bottom: 16px !important; }
          .print-invoice table td, .print-invoice table th { color: #000 !important; }
          .print-invoice .inv-total, .print-invoice .inv-subtotal, .print-invoice .inv-amount { color: #000 !important; }
          .print-invoice .inv-muted { color: #444 !important; }
        }
      `}</style>
      <div className="print-invoice">
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontSize: 20 }}>Weekly Invoice</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" style={{ padding: '7px 12px', fontSize: 12 }} onClick={() => setWeekOffset(w => w + 1)}>← Prev</button>
            <button className="btn-secondary" style={{ padding: '7px 12px', fontSize: 12 }} disabled={weekOffset === 0} onClick={() => setWeekOffset(w => w - 1)}>Next →</button>
            <button className="btn-primary" style={{ padding: '7px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => window.print()}>
              <Printer size={13} /> Print All
            </button>
          </div>
        </div>

        <Hint id="invoice">Invoices build automatically from driver logs. Use the arrows to view past weeks. Logs are sorted by date then truck number. Download PDF to send to your customer.</Hint>

        <div className="no-print field" style={{ marginBottom: 16 }}>
          <label>Filter by Customer</label>
          <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)}>
            <option value="all">All Customers</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 22, textTransform: 'uppercase' }}>{settings.companyName || 'Daily Driver Pulse'}</div>
              {settings.companyAddress && <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{settings.companyAddress}</div>}
              {(settings.companyCity || settings.companyState || settings.companyZip) && (
                <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13 }}>{[settings.companyCity, settings.companyState, settings.companyZip].filter(Boolean).join(', ')}</div>
              )}
              {settings.companyPhone && <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13 }}>{settings.companyPhone}</div>}
              {settings.companyEmail && <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13 }}>{settings.companyEmail}</div>}
              <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Week of {format(weekStart, 'MMM d')} to {format(weekEnd, 'MMM d, yyyy')}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 12 }}>Generated: {format(new Date(), 'MMM d, yyyy')}</div>
              <div className="inv-total" style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 28, color: 'var(--accent)', marginTop: 4 }}>Grand Total: ${grandTotal.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {byCustomer.length === 0 ? (
          <div className="card no-print" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <FileText size={32} color="var(--text-dim)" style={{ marginBottom: 10 }} />
            <p style={{ color: 'var(--text-muted)' }}>No logs for this week.</p>
          </div>
        ) : (
          byCustomer.map(c => {
            const invKey = c.id + '_' + thisWeekKey;
            const invoiceNum = invoiceNums[invKey] || '...';
            return (
              <div key={c.id} className="card" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 4 }}>Bill To</div>
                    <h3 style={{ fontSize: 18, marginBottom: 2 }}>{c.name}</h3>
                    {c.address && <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.address}</div>}
                    {(c.city || c.state || c.zip) && <div className="inv-muted" style={{ color: 'var(--text-muted)', fontSize: 13 }}>{[c.city, c.state, c.zip].filter(Boolean).join(', ')}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 600, fontSize: 14 }}>Invoice #{invoiceNum}</div>
                    <div className="inv-subtotal" style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 22, color: 'var(--success)' }}>${c.subtotal.toFixed(2)}</div>
                    <button className="no-print btn-secondary" onClick={() => handleDownloadPDF(c, invoiceNum)} disabled={pdfLoading === c.id || invoiceNum === '...'}
                      style={{ marginTop: 8, padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Download size={12} />{pdfLoading === c.id ? 'Generating...' : 'Download PDF'}
                    </button>
                  </div>
                </div>
                <hr className="divider" style={{ margin: '10px 0' }} />
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: 'var(--text-dim)', fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      <th style={{ textAlign: 'left', paddingBottom: 6 }}>Date</th>
                      <th style={{ textAlign: 'left', paddingBottom: 6 }}>Job #</th>
                      <th style={{ textAlign: 'left', paddingBottom: 6 }}>Truck</th>
                      <th style={{ textAlign: 'left', paddingBottom: 6 }}>Work</th>
                      <th style={{ textAlign: 'left', paddingBottom: 6 }}>Notes</th>
                      <th style={{ textAlign: 'left', paddingBottom: 6 }}>Type</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6 }}>Qty</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6 }}>Rate</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6 }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.logs.map(l => {
                      const unitType = l.unitType || 'Hours';
                      const qty = parseFloat(l.quantity || l.hours || 0);
                      const rate = l.customRate ? parseFloat(l.customRate) : getRate(c, unitType);
                      const amt = (qty * rate).toFixed(2);
                      return (
                        <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td className="inv-muted" style={{ padding: '7px 8px 7px 0', color: 'var(--text-muted)' }}>{l.date}</td>
                          <td style={{ padding: '7px 8px 7px 0', color: 'var(--text-muted)' }}>{l.jobNumber || '-'}</td>
                          <td className="inv-muted" style={{ padding: '7px 8px 7px 0', color: 'var(--text-muted)' }}>{l.truckNumber || '-'}</td>
                          <td style={{ padding: '7px 8px 7px 0', maxWidth: 120 }}>{l.workDescription}</td>
                          <td className="inv-muted" style={{ padding: '7px 8px 7px 0', maxWidth: 100, color: 'var(--text-muted)' }}>{l.notes || '-'}</td>
                          <td style={{ padding: '7px 8px 7px 0' }}><span className="tag tag-orange" style={{ fontSize: 10 }}>{unitType}</span></td>
                          <td style={{ padding: '7px 8px 7px 0', textAlign: 'right' }}>{qty}</td>
                          <td style={{ padding: '7px 8px 7px 0', textAlign: 'right', color: 'var(--text-muted)' }}>
                            ${rate.toFixed(2)}{l.customRate && <span style={{ color: 'var(--accent)', fontSize: 10 }}> *</span>}
                          </td>
                          <td className="inv-amount" style={{ padding: '7px 0', textAlign: 'right', color: 'var(--success)' }}>${amt}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {c.logs.some(l => l.photoPreview) && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 10 }}>Work Slip Photos</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                      {c.logs.filter(l => l.photoPreview).map(l => (
                        <div key={l.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                          <img src={l.photoPreview} alt={'Slip ' + l.date} style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                          <div style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Barlow Condensed' }}>{l.date} · Truck #{l.truckNumber}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

function AllLogs() {
  const { logs, customers, deleteLog, updateLog } = useApp();
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  const startEdit = (l) => { setEditing(l.id); setEditForm({ ...l }); };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async () => { await updateLog(editing, editForm); setEditing(null); setEditForm({}); };
  const setField = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));
  const handleDelete = async (id) => {
    if (confirmDelete !== id) { setConfirmDelete(id); return; }
    await deleteLog(id);
    setConfirmDelete(null);
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, marginBottom: 16 }}>All Submitted Logs</h2>
      <Hint id="logs">You can edit or delete any log here. When editing, use the Custom Rate field to set a specific rate for that load — leave it blank to use the customer's default rate.</Hint>
      {logs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ color: 'var(--text-muted)' }}>No logs yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {logs.map(l => {
            const customer = customers.find(c => c.id === l.customerId);
            const unitType = l.unitType || 'Hours';
            const qty = parseFloat(l.quantity || l.hours || 0);
            const rate = l.customRate ? parseFloat(l.customRate) : (customer ? getRate(customer, unitType) : 0);
            const amt = (qty * rate).toFixed(2);
            return (
              <div key={l.id} className="card">
                {editing === l.id ? (
                  <div>
                    <div className="grid-2">
                      <div className="field"><label>Driver Name</label><input value={editForm.driverName || ''} onChange={setField('driverName')} /></div>
                      <div className="field"><label>Date</label><input type="date" value={editForm.date || ''} onChange={setField('date')} /></div>
                    </div>
                    <div className="grid-2">
                      <div className="field">
                        <label>Customer</label>
                        <select value={editForm.customerId || ''} onChange={e => {
                          const c = customers.find(c => c.id === e.target.value);
                          setEditForm(f => ({ ...f, customerId: e.target.value, customerName: c ? c.name : '' }));
                        }}>
                          <option value="">— Select —</option>
                          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="field"><label>Job Number</label><input value={editForm.jobNumber || ''} onChange={setField('jobNumber')} /></div>
                    </div>
                    <div className="grid-2">
                      <div className="field"><label>Truck Number</label><input value={editForm.truckNumber || ''} onChange={setField('truckNumber')} /></div>
                      <div className="field">
                        <label>Pay Type</label>
                        <select value={editForm.unitType || 'Hours'} onChange={setField('unitType')}>
                          <option value="Hours">Hours</option>
                          <option value="Tons">Tons</option>
                          <option value="Loads">Loads</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid-2">
                      <div className="field"><label>Quantity</label><input type="number" step="0.5" value={editForm.quantity || editForm.hours || ''} onChange={setField('quantity')} /></div>
                      <div className="field">
                        <label>Custom Rate (optional)</label>
                        <input type="number" step="0.01" min="0" value={editForm.customRate || ''} onChange={setField('customRate')} placeholder="Leave blank for default" />
                      </div>
                    </div>
                    <div className="field"><label>Work Description</label><textarea value={editForm.workDescription || ''} onChange={setField('workDescription')} rows={2} /></div>
                    <div className="field"><label>Notes</label><textarea value={editForm.notes || ''} onChange={setField('notes')} rows={2} /></div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-primary" onClick={saveEdit} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px' }}><Check size={13} /> Save</button>
                      <button className="btn-secondary" onClick={cancelEdit} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px' }}><X size={13} /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 15 }}>{l.customerName || 'Unknown'}</span>
                        <span className="tag tag-orange" style={{ fontSize: 10 }}>{unitType}</span>
                        {l.jobNumber && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Job #{l.jobNumber}</span>}
                        {l.customRate && <span className="tag tag-orange" style={{ fontSize: 10 }}>Custom Rate</span>}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                        {l.date} · Truck #{l.truckNumber} · {qty} {unitType}
                        {l.customRate && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>@ ${parseFloat(l.customRate).toFixed(2)}/custom</span>}
                      </div>
                      <div style={{ fontSize: 13, marginTop: 2 }}>{l.workDescription}</div>
                      {l.notes && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{l.notes}</div>}
                    </div>
                    {customer && <span className="tag tag-green">${amt}</span>}
                    <button className="btn-secondary" onClick={() => startEdit(l)} style={{ padding: '5px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><Pencil size={12} /> Edit</button>
                    {confirmDelete === l.id ? (
                      <>
                        <button className="btn-danger" onClick={() => handleDelete(l.id)} style={{ padding: '5px 12px', fontSize: 12 }}>Confirm</button>
                        <button className="btn-secondary" onClick={() => setConfirmDelete(null)} style={{ padding: '5px 10px', fontSize: 12 }}><X size={12} /></button>
                      </>
                    ) : (
                      <button className="btn-danger" onClick={() => handleDelete(l.id)}><Trash2 size={12} /></button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function AdminMenu({ tabs, tab, setTab }) {
  const [open, setOpen] = React.useState(false);
  const current = tabs.find(t => t.id === tab);

  return (
    <div style={{ marginBottom: 24, position: 'relative' }}>
      {/* Mobile dropdown button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '14px 16px',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', color: 'var(--accent)',
          fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 15,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {current?.icon} {current?.label}
        </span>
        <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>{open ? '✕' : '☰'}</span>
      </button>

      {/* Dropdown menu */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 98 }}
          />
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', zIndex: 99,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            overflow: 'hidden', marginTop: 4,
          }}>
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', padding: '14px 16px',
                  background: t.id === tab ? 'var(--bg3)' : 'transparent',
                  border: 'none',
                  borderLeft: t.id === tab ? '3px solid var(--accent)' : '3px solid transparent',
                  color: t.id === tab ? 'var(--accent)' : 'var(--text-muted)',
                  fontFamily: 'Barlow Condensed', fontWeight: 600, fontSize: 15,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  textAlign: 'left',
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const { isAdmin } = useApp();
  const [tab, setTab] = useState('invoice');

  if (!isAdmin) return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
      <p style={{ color: 'var(--text-muted)' }}>Access denied.</p>
    </div>
  );

  const tabs = [
    { id: 'invoice', label: 'Weekly Invoice', icon: <DollarSign size={14} /> },
    { id: 'customers', label: 'Customers', icon: <Users size={14} /> },
    { id: 'logs', label: 'All Logs', icon: <FileText size={14} /> },
    { id: 'payments', label: 'Payments', icon: <CreditCard size={14} /> },
    { id: 'reports', label: 'Reports', icon: <TrendingUp size={14} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={14} /> },
  ];

  return (
    <div className="page-wide">
      <OnboardingModal />
      <div className="admin-header" style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, marginBottom: 4 }}>Admin Dashboard</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Manage customers, view logs, and generate invoices.</p>
      </div>
      <AdminMenu tabs={tabs} tab={tab} setTab={setTab} />
      {tab === 'invoice' && <WeeklyInvoice />}
      {tab === 'customers' && <CustomerManager />}
      {tab === 'logs' && <AllLogs />}
      {tab === 'payments' && <Payments />}
      {tab === 'reports' && <Reports />}
      {tab === 'settings' && (
        <div>
          <Hint id="settings">Your Company Code is in the Settings tab. Share it with your drivers when they sign up — they'll need it to link to your company. You can also set your company info here for invoice letterheads.</Hint>
          <AdminSettings />
        </div>
      )}
    </div>
  );
}
